// Merged from dev-scrips-pm-react src/billing/services/use-invoice-orders.ts (PROD-3312). PA keeps a null-tolerant id (the encounter wrap-up gate passes null).
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, get, post } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import { invalidateBalanceViews } from '@/billing/services/invalidate-balance-views';
import type {
  AddInvoiceOrdersRequest,
  DismissInvoiceOrdersRequest,
  InvoiceOrders,
  InvoiceOrdersError,
  InvoiceOrdersErrorCode,
  InvoiceOrdersMutationResponse,
  RestoreInvoiceOrdersRequest,
  RevertInvoiceOrdersRequest,
} from '@/shared/types/billing';

/**
 * Order to Invoice (PROD-2583, T7): the pending orders of one invoice.
 *
 *   GET  /api/Billing/Invoice/{invoiceId}/Orders
 *   POST /api/Billing/Invoice/{invoiceId}/Orders/add       single + bulk, all or nothing
 *   POST /api/Billing/Invoice/{invoiceId}/Orders/dismiss   optional reason + note
 *   POST /api/Billing/Invoice/{invoiceId}/Orders/restore   Dismissed -> Pending
 *   POST /api/Billing/Invoice/{invoiceId}/Orders/revert    Added -> Pending (also "Drop")
 *
 * Every POST is Draft only and returns `{ invoice, orders }`: the re-priced
 * invoice and the fresh orders read.
 *
 * **Invalidation.** Each mutation invalidates the invoice detail key by its
 * `['invoice','detail',invoiceId]` prefix (`useInvoice` appends a
 * `resubmissionType` slot, so an exact key would miss it; same as
 * `useCancelInvoice`) and the orders key. Refreshing both keeps the services
 * table, the totals and the pending list in step.
 *
 * One strategy, no cache seeding: the response's `orders` is NOT written into
 * the cache, because the invalidation refetches that key straight away and
 * would replace it anyway.
 *
 * **Errors** (`{ code, message, items }`). Every 409 in the contract
 * (`AlreadyAdded`, `NotPending`, `NotDismissed`, `NotAdded`,
 * `ServiceAlreadyOnInvoice`) means the row changed under us, e.g. another
 * biller got there first, so both keys are refetched and the toast explains why
 * the row moved. The message is shown verbatim only for a code in the Billing
 * contract (`InvoiceOrdersErrorCode`): those messages are written by the Orders
 * endpoints for billers, and the remove-guard messages on revert are the same
 * ones the existing remove path shows. Any other body falls back to the generic
 * message, so an unexpected server error never reaches the screen raw.
 */

export const invoiceOrdersKey = (invoiceId: string | null | undefined) =>
  ['invoice', 'orders', invoiceId ?? null] as const;

export const invoiceDetailPrefixKey = (invoiceId: string) =>
  ['invoice', 'detail', invoiceId] as const;

const ordersUrl = (invoiceId: string) => `${BILLING_BASE}/Invoice/${invoiceId}/Orders`;

const FALLBACK_MESSAGE = 'Something went wrong, please contact Scrips support.';

/** The contract's error codes: the only ones whose message is shown verbatim. */
const CONTRACT_ERROR_CODES: ReadonlySet<string> = new Set<InvoiceOrdersErrorCode>([
  'InvalidRequest',
  'InvoiceNotDraft',
  'AddNotAllowed',
  'RemoveNotAllowed',
  'InvoiceNotFound',
  'AlreadyAdded',
  'NotPending',
  'NotDismissed',
  'NotAdded',
  'ServiceAlreadyOnInvoice',
  'NoEncounter',
  'OrderNotInPlan',
  'ServiceRequired',
  'ServiceNotCandidate',
  'ServiceNotAvailable',
]);

/**
 * PROD-2678: orders are placed in another app (practitioner-react's A&P), so a
 * biller who leaves this tab to sign a plan must see the new orders on return.
 * Always stale, re-read on every mount and whenever the window regains focus,
 * overriding the global opt-outs on purpose (as `use-calendar-slots.ts`
 * `NO_CACHE` does). `gcTime` is left at its default: several observers share
 * this key (the section, the indicative total, the services table).
 */
export const INVOICE_ORDERS_FRESHNESS = {
  staleTime: 0,
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
} as const;

export function useInvoiceOrders(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: invoiceOrdersKey(invoiceId),
    queryFn: ({ signal }) => get<InvoiceOrders>(ordersUrl(invoiceId as string), { signal }),
    enabled: Boolean(invoiceId),
    ...INVOICE_ORDERS_FRESHNESS,
  });
}

/**
 * Reads the Orders error body. Returns null for anything that is not an
 * `ApiError` carrying `{ code, message }` (network failure, HTML 502, ...).
 */
export function parseInvoiceOrdersError(err: unknown): InvoiceOrdersError | null {
  if (!(err instanceof ApiError) || !err.body) return null;
  try {
    const body = JSON.parse(err.body) as Partial<InvoiceOrdersError>;
    if (typeof body?.code !== 'string' || typeof body.message !== 'string') return null;
    return { code: body.code, message: body.message, items: body.items ?? [] };
  } catch {
    return null;
  }
}

/** The text to show for an Orders failure: the server message, verbatim, for a
 *  contract code; the generic message otherwise. */
export function invoiceOrdersErrorMessage(err: unknown): string {
  const parsed = parseInvoiceOrdersError(err);
  if (!parsed || !CONTRACT_ERROR_CODES.has(parsed.code) || !parsed.message) return FALLBACK_MESSAGE;
  return parsed.message;
}

function invalidateOrderViews(qc: QueryClient, invoiceId: string) {
  void qc.invalidateQueries({ queryKey: invoiceDetailPrefixKey(invoiceId) });
  void qc.invalidateQueries({ queryKey: invoiceOrdersKey(invoiceId) });
}

interface OrdersMutationVars<TRequest> {
  invoiceId: string;
  request: TRequest;
}

function useOrdersMutation<TRequest>(action: 'add' | 'dismiss' | 'restore' | 'revert') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, request }: OrdersMutationVars<TRequest>) =>
      post<InvoiceOrdersMutationResponse>(`${ordersUrl(invoiceId)}/${action}`, request),
    onSuccess: (_data, { invoiceId }) => {
      invalidateOrderViews(qc, invoiceId);
      // Add and revert re-price the invoice: the workbench rows and the Total
      // Unpaid / balance cards read those totals under their own keys (PROD-1809).
      void qc.invalidateQueries({ queryKey: ['claim-workbench'] });
      invalidateBalanceViews(qc);
    },
    onError: (err, { invoiceId }) => {
      if (err instanceof ApiError && err.status === 409) invalidateOrderViews(qc, invoiceId);
      console.error(`[invoice-orders:${action}]`, err);
      toast.error(invoiceOrdersErrorMessage(err));
    },
  });
}

export type AddOrdersVars = OrdersMutationVars<AddInvoiceOrdersRequest>;
export type DismissOrdersVars = OrdersMutationVars<DismissInvoiceOrdersRequest>;
export type RestoreOrdersVars = OrdersMutationVars<RestoreInvoiceOrdersRequest>;
export type RevertOrdersVars = OrdersMutationVars<RevertInvoiceOrdersRequest>;

/** Add one or many pending orders to the invoice (all or nothing, max 100). */
export function useAddOrders() {
  return useOrdersMutation<AddInvoiceOrdersRequest>('add');
}

/** Dismiss one or many pending orders, with an optional reason and note. */
export function useDismissOrders() {
  return useOrdersMutation<DismissInvoiceOrdersRequest>('dismiss');
}

/** Move dismissed orders back to Pending. */
export function useRestoreOrders() {
  return useOrdersMutation<RestoreInvoiceOrdersRequest>('restore');
}

/** Take added orders off the invoice and back to Pending (the "Drop" action). */
export function useRevertOrders() {
  return useOrdersMutation<RevertInvoiceOrdersRequest>('revert');
}
